import React, { type ReactNode } from "react";
import {
  AccordionItem as ChakraAccordionItem,
  AccordionButton,
  AccordionPanel,
  Flex,
  Text,
  AspectRatio,
  Box,
  type TextProps,
  type AspectRatioProps,
} from "@chakra-ui/react";
import { remCalc } from "theme/utils";
import { ChevronDownIcon } from "ui/icons";
import { type Theme } from "contexts/color-theme";

export type AccordionItemContent = {
  /**
   * Unique identifier for the accordion item.
   * Required if there are multiple item titles that are the same.
   */
  id?: string;

  /**
   * Title of the accordion item.
   */
  title: string;

  /**
   * The HTML element to render the title as.
   *
   * @default "h3"
   */
  titleAs?: TextProps["as"];

  /**
   * The expandable content of the accordion item.
   */
  content: ReactNode;

  /**
   * The media to render in the accordion item. Each item can have different media.
   * Can be either an image or lottie animation, but must be a single JSX Element.
   *
   * Only used in `MediaAccordion` with type `dynamic` on mobile screens.
   */
  media?: JSX.Element;

  /**
   * The aspect ratio of the media.
   *
   * @default 1 / 1
   */
  mediaRatio?: AspectRatioProps["ratio"];
};

type AccordionItemProps = Omit<AccordionItemContent, "title" | "id"> & {
  /**
   * The title node for the accordion item, which can be any valid ReactNode.
   */
  title: ReactNode;

  /**
   * The color theme of the accordion item.
   *
   * @default "light"
   */
  theme?: Theme;
};

export function AccordionItem({
  theme = "light",
  title,
  titleAs = "h3",
  content,
  media,
  mediaRatio = 1 / 1,
}: AccordionItemProps) {
  return (
    <ChakraAccordionItem
      borderBottom="none"
      borderColor={`brand.border.${theme}`}
      _first={{
        borderTop: "0px",
      }}
    >
      {/* Chakra requires accordion buttons to be wrapped in an element with role `heading` for a11y. */}
      <Box scrollMarginTop="huge" as={titleAs} role="heading">
        <AccordionButton
          borderRadius="tinier" // For focus states
          color={`brand.text.${theme}`}
          display="flex"
          alignItems="center"
          gap="small"
          justifyContent="space-between"
          textAlign="left"
          px={0}
          py="small"
          _hover={{ bg: "transparent" }}
          _expanded={{
            svg: { transform: "rotate(180deg)" },
          }}
        >
          <Text as="span" textStyle="headline3" fontSize="brand.md">
            {title}
          </Text>
          <Flex
            align="center"
            borderRadius="full"
            bg="brand.backgroundSecondary.light" // Always use light theme for buttons
            color="brand.text.light"
            boxSize={remCalc(42)}
            flexShrink={0}
            justify="center"
          >
            <ChevronDownIcon transition="transform 0.25s ease-in-out" />
          </Flex>
        </AccordionButton>
      </Box>
      <AccordionPanel color={`brand.textSecondary.${theme}`} textStyle="caption1" mb="small" p={0}>
        <Box pr={{ base: "bigger", md: "biggest" }}>{content}</Box>
        {media && (
          <AspectRatio
            display={{ base: "block", md: "none" }}
            borderRadius="small"
            ratio={mediaRatio}
            flex={1}
            w="100%"
            maxW={{ base: "mediaMaxW.mobile" }}
            mx="auto"
            mt="small"
            pos="relative"
            overflow="hidden"
          >
            {media}
          </AspectRatio>
        )}
      </AccordionPanel>
    </ChakraAccordionItem>
  );
}
